import React, { useState, useEffect } from 'react';
import Modal from '../molecules/Modal';
import InfoCard from '../molecules/InfoCard';
import LoadingSpinner from '../ui/LoadingSpinner';
import ActivityLogTimeline from '../molecules/ActivityLogTimeline';
import ContactCreationDialog from '../molecules/ContactCreationDialog';
import { accountsService } from '@/services/api/api';
import { useAuthStore } from '@/stores/authStore';
import logger from '@/utils/secureLogger';

const AccountDetailsModal = ({ account, open, onClose, formatLabel }) => {
  const { currentUser } = useAuthStore();
  const canEdit = currentUser?.groups?.includes('CES_ADMIN') || false;
  
  const [accountDetails, setAccountDetails] = useState(null);
  const [originalAccountData, setOriginalAccountData] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activityError, setActivityError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({
    CloudProvider: [],
    TypeName: [],
    OpcoName: [],
    DomainName: [],
    Code: [],
    AccountsGroupName: [],
    Decommissioned: ['Active', 'Inactive'],
    AccountOwner: [],
    Accountable: [],
    BillingResponsible: [],
    OperationsResponsible: [],
    BudgetOwner: [],
    AlternateContacts: []
  });
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);


  useEffect(() => {
    if (open && account?.AccountId) {
      loadAccountDetails();
      loadActivityLog();
    } else if (!open) {
      setIsEditing(false);
      setEditedData({});
    }
  }, [open, account?.AccountId]);

  useEffect(() => {
    if (isEditing && !canEdit) {
      setIsEditing(false);
      setEditedData({});
    }
  }, [canEdit, isEditing]);

  const loadAccountDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountsService.getSingleAccount(account.AccountId);
      const accountData = response.data[0]; 
      setOriginalAccountData(accountData);
      
      const ownerResp = accountData.Responsibles?.find(r => r.ResponsibilityType === 'owner');
      const accountableResp = accountData.Responsibles?.find(r => r.ResponsibilityType === 'accountable');
      const billingResp = accountData.Responsibles?.find(r => r.ResponsibilityType === 'billing');
      const operationsResp = accountData.Responsibles?.find(r => r.ResponsibilityType === 'operations');
      const budgetOwnerResp = accountData.Responsibles?.find(r => r.ResponsibilityType === 'budgetOwner');
      const alternateResps = accountData.Responsibles?.filter(r => r.ResponsibilityType === 'alternate') || [];
      
      const flattenedData = {
        CloudProvider: accountData.Cloud?.[0]?.PublicCloudUid || null,
        AccountId: accountData.AccountId || null,
        AccountName: accountData.AccountName || null,
        Budget: accountData.Budget || null,
        Decommissioned: accountData.Decommissioned || false,
        DecommissioningDate: accountData.DecommissioningDate || null,
        Description: accountData.Description || null,
        ITDemandTicket: accountData.ITDemandTicket || null,
        BudgetLineReference: accountData.BudgetLineReference || null,
        TypeName: accountData.Type?.[0]?.TypeName || null,
        OpcoName: accountData.Opco?.[0]?.OpcoName || null,
        DomainName: accountData.Domain?.[0]?.DomainName || null,
        Code: accountData.Cost_Center?.[0]?.Code || null,
        AccountsGroupName: accountData.Group?.[0]?.AccountsGroupName || null,
        AccountOwner: ownerResp ? `${ownerResp.FirstName} ${ownerResp.LastName}${ownerResp.EmailAddress ? ` (${ownerResp.EmailAddress})` : ''}` : null,
        Accountable: accountableResp ? `${accountableResp.FirstName} ${accountableResp.LastName}${accountableResp.EmailAddress ? ` (${accountableResp.EmailAddress})` : ''}` : null,
        BudgetOwner: budgetOwnerResp ? `${budgetOwnerResp.FirstName} ${budgetOwnerResp.LastName}${budgetOwnerResp.EmailAddress ? ` (${budgetOwnerResp.EmailAddress})` : ''}` : null,
        BillingResponsible: billingResp ? `${billingResp.FirstName} ${billingResp.LastName}${billingResp.EmailAddress ? ` (${billingResp.EmailAddress})` : ''}` : null,
        OperationsResponsible: operationsResp ? `${operationsResp.FirstName} ${operationsResp.LastName}${operationsResp.EmailAddress ? ` (${operationsResp.EmailAddress})` : ''}` : null,
        AlternateContacts: alternateResps.map(alt => `${alt.FirstName} ${alt.LastName}${alt.EmailAddress ? ` (${alt.EmailAddress})` : ''}`)
      };
      
      setAccountDetails(flattenedData);
    } catch (err) {
      setError('Error loading account details');
      logger.error('Error loading account details', { error: err });
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLog = async () => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const response = await accountsService.getActivityLog(account.AccountId);
      setActivityLog(response.data || []);
    } catch (err) {
      setActivityError('Error loading activity log');
      logger.error('Error loading activity log', { error: err });
    } finally {
      setActivityLoading(false);
    }
  };

  if (!open || !account) return null;

  const displayAccount = accountDetails || account;

  const AccountIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
    </svg>
  );

  const ActivityIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
  );

  const EditIcon = () => (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  );

  const loadDropdownOptions = async () => {
    setDropdownLoading(true);
    try {
      const [opcos, types, domains, contacts, cloudProviders, costCenters, groups] = await Promise.all([
        accountsService.getOpcos(),
        accountsService.getTypes(),
        accountsService.getDomains(),
        accountsService.getContacts(),
        accountsService.getCloudProviders(),
        accountsService.getCostCenters(),
        accountsService.getGroups()
      ]);

      const sortedContacts = contacts.data?.map(c => `${c.FirstName} ${c.LastName}${c.EmailAddress ? ` (${c.EmailAddress})` : ''}`).sort() || [];
      
      setDropdownOptions({
        CloudProvider: cloudProviders.data?.map(cp => cp.PublicCloudUid) || [],
        TypeName: types.data?.map(t => t.TypeName) || [],
        OpcoName: opcos.data?.map(o => o.OpcoName) || [],
        DomainName: domains.data?.map(d => d.DomainName) || [],
        Code: costCenters.data?.map(cc => cc.Code) || [],
        AccountsGroupName: groups.data?.map(g => g.AccountsGroupName) || [],
        Decommissioned: ['Active', 'Inactive'],
        AccountOwner: sortedContacts,
        Accountable: sortedContacts,
        BillingResponsible: sortedContacts,
        OperationsResponsible: sortedContacts,
        BudgetOwner: sortedContacts,
        AlternateContacts: sortedContacts
      });
    } catch (err) {
      logger.error('Error loading dropdown options', { error: err });
    } finally {
      setDropdownLoading(false);
    }
  };

  const handleEdit = async () => {
    setEditedData(displayAccount);
    await loadDropdownOptions();
    setIsEditing(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setIsEditing(false);
    try {
      const newValues = {};
      
      Object.keys(editedData).forEach(key => {
        if (key === 'AlternateContacts') {
          const originalAlternates = Array.isArray(accountDetails[key]) ? accountDetails[key] : [];
          const editedAlternates = Array.isArray(editedData[key]) ? editedData[key] : [];
          const hasChanged =
            originalAlternates.length !== editedAlternates.length ||
            editedAlternates.some((alt, i) => alt !== originalAlternates[i]);

          if (hasChanged) {
            editedAlternates.forEach(alt => {
              const contactMatch = alt?.match(/^(.+?)\s+(.+?)\s*\((.+?)\)$/);
              if (contactMatch) {
                const [, firstName, lastName, email] = contactMatch;
                newValues['Responsibles'] = newValues['Responsibles'] || [];
                newValues['Responsibles'].push({
                  FirstName: firstName,
                  LastName: lastName,
                  EmailAddress: email,
                  ResponsibilityType: 'alternate'
                });
              }
            });
          }
          return;
        }

        if (editedData[key] !== accountDetails[key]) {
          if (['AccountOwner', 'Accountable', 'BillingResponsible', 'OperationsResponsible', 'BudgetOwner'].includes(key)) {
            const responsibilityTypeMap = {
              'AccountOwner': 'owner',
              'Accountable': 'accountable', 
              'BillingResponsible': 'billing',
              'OperationsResponsible': 'operations',
              'BudgetOwner': 'budgetOwner'
            };
            const contactMatch = editedData[key]?.match(/^(.+?)\s+(.+?)\s*\((.+?)\)$/);
            if (contactMatch) {
              const [, firstName, lastName, email] = contactMatch;
              newValues['Responsibles'] = newValues['Responsibles'] || [];
              newValues['Responsibles'].push({
                FirstName: firstName,
                LastName: lastName,
                EmailAddress: email,
                ResponsibilityType: responsibilityTypeMap[key]
              });
            }
          } else if (key === 'CloudProvider') {
            newValues['Cloud'] = [{ PublicCloudUid: editedData[key] }];
          } else if (key === 'TypeName') {
            newValues['Type'] = [{ TypeName: editedData[key] }];
          } else if (key === 'OpcoName') {
            newValues['Opco'] = [{ OpcoName: editedData[key] }];
          } else if (key === 'DomainName') {
            newValues['Domain'] = [{ DomainName: editedData[key] }];
          } else if (key === 'Code') {
            newValues['Cost_Center'] = [{ Code: editedData[key] }];
          } else if (key === 'AccountsGroupName') {
            newValues['Group'] = [{ AccountsGroupName: editedData[key] }];
          } else {
            newValues[key] = key === 'Budget' ? editedData[key]?.toString() : editedData[key];
          }
        }
      });
      
      const payload = {
        "Executed by": currentUser?.email || currentUser?.username || '',
        "AccountId": editedData.AccountId,
        "New Values": newValues
      };
      
      await accountsService.updateAccount(account.AccountId, payload);
      await loadAccountDetails();
      await loadActivityLog()
      setIsEditing(false);
    } catch (err) {
      logger.error('Error saving account', { error: err });
      setError('Error saving account changes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedData({});
    setIsEditing(false);
    setDropdownLoading(false);
  };

  const handleFieldChange = (key, value) => {
    if (key === 'Decommissioned') {
      const booleanValue = value === 'Inactive';
      setEditedData(prev => ({ ...prev, [key]: booleanValue }));
    } else {
      setEditedData(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleAlternateChange = (index, value) => {
    setEditedData(prev => {
      const current = Array.isArray(prev.AlternateContacts) ? [...prev.AlternateContacts] : [];
      current[index] = value;
      return { ...prev, AlternateContacts: current };
    });
  };

  const handleAddAlternate = () => {
    setEditedData(prev => {
      const current = Array.isArray(prev.AlternateContacts) ? [...prev.AlternateContacts] : [];
      return { ...prev, AlternateContacts: [...current, ''] };
    });
  };

  const handleRemoveAlternate = (index) => {
    setEditedData(prev => {
      const current = Array.isArray(prev.AlternateContacts) ? [...prev.AlternateContacts] : [];
      current.splice(index, 1);
      return { ...prev, AlternateContacts: current };
    });
  };

  const handleNewContact = () => {
    setIsContactDialogOpen(true);
  };

  const handleContactCreated = async (newContact) => {
    await loadDropdownOptions();
  };



  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Account Details"
      subtitle={`Account ID: ${account.AccountId || account.id || 'N/A'}`}
      maxWidth="max-w-7xl"
      maxHeight="max-h-[90vh]"
    >
      <div className="mb-4 flex justify-end space-x-2">
        {!isEditing && canEdit ? (
          <button
            onClick={handleEdit}
            disabled={dropdownLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {dropdownLoading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <EditIcon />
            )}
            <span className="ml-2">{dropdownLoading ? 'Loading...' : 'Edit'}</span>
          </button>
        ) : isEditing && canEdit ? (
          <>
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save
            </button>
          </>
        ) : null}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information Column */}
        <div className="space-y-4">
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner message="Loading account details..." />
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          {!loading && !error && (
            <InfoCard
              title="Account Information"
              icon={<AccountIcon />}
            >
              {Object.entries(isEditing ? editedData : displayAccount)
                .filter(([key]) => !['AccountOwner', 'Accountable', 'BillingResponsible', 'OperationsResponsible', 'BudgetOwner', 'AlternateContacts'].includes(key))
                .map(([key, value]) => {
                let displayValue = value;
                let isBadge = false;
                let badgeVariant = null;
                const isReadOnly = ['AccountId', 'AccountName','CloudProvider'].includes(key);
                
                if (key === 'Decommissioned') {
                  if (isEditing) {
                    displayValue = value === true ? 'Inactive' : 'Active';
                  } else {
                    displayValue = value === true ? 'Decommissioned' : 'Active';
                    isBadge = true;
                    badgeVariant = value === true ? 'danger' : 'success';
                  }
                } else if (key === 'Budget' && !isEditing && value !== null && value !== undefined) {
                  let budgetValue = 0;
                  if (typeof value === 'number') {
                    budgetValue = value;
                  } else if (typeof value === 'string') {
                    budgetValue = parseFloat(value.toString().replace(/[USD$,\s]/g, '')) || 0;
                  }
                  displayValue = `USD $${budgetValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                }
                
                const hasDropdownOptions = dropdownOptions[key]?.length > 0;
                
                return (
                  <InfoCard.Row 
                    key={key} 
                    label={formatLabel ? formatLabel(key) : key} 
                    value={isEditing && !isReadOnly ? (
                      hasDropdownOptions ? (
                        <select
                          value={displayValue || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select {formatLabel ? formatLabel(key) : key}</option>
                          {dropdownOptions[key].map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : key === 'DecommissioningDate' ? (
                        <input
                          type="date"
                          value={displayValue ? new Date(displayValue).toISOString().split('T')[0] : ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : key === 'Description' ? (
                        <textarea
                          value={displayValue || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          rows={5}
                          className="w-full min-w-0 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
                          placeholder="Enter account description..."
                          style={{ minHeight: '100px', width: '100%' }}
                        />
                      ) : key === 'Budget' ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={displayValue || ''}
                          onChange={(e) => handleFieldChange(key, parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <input
                          type="text"
                          value={displayValue || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      )
                    ) : (
                      displayValue || 'N/A'
                    )}
                    badge={isBadge && !isEditing}
                    badgeVariant={badgeVariant}
                  />
                );
              })}
            </InfoCard>
          )}
        </div>

        {/* Contacts and Activity Log Column */}
        <div className="space-y-4">
          {/* Contacts Section */}
          {!loading && !error && (
            <InfoCard
              title="Contacts"
              icon={<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>}
            >
              {isEditing && (
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    onClick={handleNewContact}
                    className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    New Contact
                  </button>
                </div>
              )}
              {Object.entries(isEditing ? editedData : displayAccount)
                .filter(([key]) => ['AccountOwner', 'Accountable', 'BillingResponsible', 'OperationsResponsible', 'BudgetOwner', 'AlternateContacts'].includes(key))
                .map(([key, value]) => {
                const hasDropdownOptions = dropdownOptions[key]?.length > 0;
                
                return (
                  <InfoCard.Row 
                    key={key} 
                    label={formatLabel ? formatLabel(key) : key} 
                    value={key === 'AlternateContacts' ? (
                      isEditing ? (
                        <div className="space-y-2">
                          {(Array.isArray(value) ? value : []).map((contact, index) => (
                            <div key={index} className="flex items-center gap-2">
                              {hasDropdownOptions ? (
                                <select
                                  value={contact || ''}
                                  onChange={(e) => handleAlternateChange(index, e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  <option value="">Select {formatLabel ? formatLabel(key) : key}</option>
                                  {dropdownOptions[key].map((option, optIndex) => (
                                    <option key={optIndex} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={contact || ''}
                                  onChange={(e) => handleAlternateChange(index, e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveAlternate(index)}
                                className="flex-shrink-0 px-2 py-1 text-sm text-red-600 hover:text-red-800 focus:outline-none"
                                aria-label="Remove alternate contact"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleAddAlternate}
                            className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 focus:outline-none"
                          >
                            + Add alternate contact
                          </button>
                        </div>
                      ) : (
                        Array.isArray(value) && value.length > 0 ? (
                          <div className="space-y-1">
                            {value.map((contact, index) => (
                              <div key={index}>
                                {contact}
                              </div>
                            ))}
                          </div>
                        ) : 'N/A'
                      )
                    ) : isEditing ? (
                      hasDropdownOptions ? (
                        <select
                          value={value || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select {formatLabel ? formatLabel(key) : key}</option>
                          {dropdownOptions[key].map((option, index) => (
                            <option key={index} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={value || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      )
                    ) : (
                      value || 'N/A'
                    )}
                  />
                );
              })}
            </InfoCard>
          )}
          
          {/* Activity Log Section */}
          {!loading && !error && (
            <InfoCard
              title="Activity Log"
              icon={<ActivityIcon />}
            >
              <div className="max-h-64 overflow-y-auto">
                <ActivityLogTimeline 
                  activities={activityLog}
                  loading={activityLoading}
                  error={activityError}
                />
              </div>
            </InfoCard>
          )}
        </div>
      </div>
      
      <ContactCreationDialog
        isOpen={isContactDialogOpen}
        onClose={() => setIsContactDialogOpen(false)}
        onContactCreated={handleContactCreated}
      />
    </Modal>
  );
};

export default AccountDetailsModal; 